import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api-client";
import { z } from "zod";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { FileSignature, Plus, ChevronRight } from "lucide-react";

export const Route = createFileRoute("/claims/")({
  component: ClaimsList,
});

const STATUS_TONE: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  draft: "outline",
  submitted: "default",
  accepted: "secondary",
  rejected: "destructive",
  paid: "secondary",
};

function ClaimsList() {
  const { data, isLoading } = useQuery({
    queryKey: ["invoices"],
    queryFn: () => apiFetch("/api/billing/invoices", { schema: z.object({ items: z.array(z.any()) }) }),
  });

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <FileSignature className="h-5 w-5" /> Feuilles de soins
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">CNSS · CNOPS · AMO · Privé · Maroc</p>
        </div>
        <Button asChild>
          <Link to="/claims/new"><Plus className="h-4 w-4 mr-1" /> Nouvelle FSE</Link>
        </Button>
      </div>

      <div className="grid gap-2">
        {isLoading ? (
          Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-20 w-full" />)
        ) : data?.items.length ? (
          data.items.map((inv: any) => (
            <Card key={inv.id}>
              <CardContent className="flex items-center justify-between p-4">
                <div>
                  <div className="font-medium">{inv.invoiceNumber}</div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {inv.patientName} · {new Date(inv.createdAt).toLocaleDateString("fr-FR")} · {inv.insuranceType ?? "Privé"}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="text-sm font-bold">{(inv.totalCents / 100).toFixed(2)} MAD</div>
                    <div className="text-xs text-muted-foreground">part assureur {(inv.insuranceShareCents / 100).toFixed(2)}</div>
                  </div>
                  <Badge variant={STATUS_TONE[inv.status] ?? "outline"}>{inv.status}</Badge>
                  <Link to={`/claims/${inv.id}`} className="text-muted-foreground hover:text-foreground">
                    <ChevronRight className="h-4 w-4" />
                  </Link>
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <div className="rounded-lg border border-dashed border-white/10 p-8 text-center text-sm text-muted-foreground">
            Aucune facture
          </div>
        )}
      </div>
    </div>
  );
}
